import React, { useState, useEffect, useCallback } from "react";
import { useWindowDimensions } from "./hooks/useWindowDimensions";
import { useSimulation } from "./hooks/useSimulation";
import { useMusicSequencer } from "./hooks/useMusicSequencer";
import { initializeSounds } from "./soundInitializer";
import StartScreen from "./components/StartScreen";
import GameOverScreen from "./components/GameOverScreen";
import GameLayout from "./components/GameLayout";

export default function HelioSonicApp() {
  const windowDimensions = useWindowDimensions();
  const {
    simulationData,
    gameOver,
    startSimulation,
    togglePause,
    updateMusicState,
    stay,
  } = useSimulation();

  const [musicStarted, setMusicStarted] = useState(false);
  const [showBottomPane, setShowBottomPane] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [wasStayed, setWasStayed] = useState(false);

  const { playBottomSound, resetSequencer } = useMusicSequencer({
    musicStarted,
    gameOver,
    isPaused: simulationData.is_paused,
    isMuted, // ミュート状態を渡す
    showBottomPane,
    tempo: simulationData.tempo,
    isDissonant: simulationData.is_dissonant,
    masterVolume: isMuted ? 0 : simulationData.master_volume,
    onMusicStateUpdate: updateMusicState,
  });

  useEffect(() => {
    initializeSounds();
  }, []);

  const handleStart = (showPane) => {
    setShowBottomPane(showPane);
    if (!musicStarted) {
      setMusicStarted(true);
      startSimulation();
      resetSequencer();
    }
  };

  const handlePauseClick = useCallback(() => {
    if (musicStarted) {
      togglePause();

      // Stayボタンの後でGoボタンが押された場合
      if (wasStayed && simulationData.is_paused) {
        setIsMuted(false); // 音楽も再開
        setWasStayed(false); // フラグをリセット
      }
    }
  }, [musicStarted, togglePause, wasStayed, simulationData.is_paused]);

  const handleBottomClick = useCallback(async () => {
    if (!musicStarted) {
      handleStart(true);
      return;
    }
    await playBottomSound();
  }, [musicStarted, playBottomSound, handleStart]);

  const handleMuteClick = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handleStayClick = useCallback(() => {
    if (musicStarted && !gameOver) {
      stay();
      setIsMuted(true);
      setWasStayed(true);
    }
  }, [musicStarted, gameOver, stay]);

  // Stayボタンの表示判定関数
  const shouldShowStayButton = useCallback(() => {
    const distance = simulationData.distance_traveled;
    return (
      (distance >= 16 && distance <= 22) ||
      (distance >= 36 && distance <= 42) ||
      (distance >= 56 && distance <= 62)
    );
  }, [simulationData.distance_traveled]);

  if (gameOver) {
    return <GameOverScreen gameOverType={gameOver} />;
  }

  if (!musicStarted) {
    return <StartScreen onStart={handleStart} />;
  }

  return (
    <GameLayout
      windowDimensions={windowDimensions}
      simulationData={simulationData}
      showBottomPane={showBottomPane}
      onPauseClick={handlePauseClick}
      onBottomClick={handleBottomClick}
      onMuteClick={handleMuteClick}
      isMuted={isMuted}
      onStayClick={handleStayClick}
      showStayButton={shouldShowStayButton()}
    />
  );
}
